import posthog from 'posthog-js';
import { useEffect } from 'react';

import { useAuthStore } from '@/stores/use-auth-store';

import { AuthRefreshError, fetchUserInfo, refreshAccessToken } from './auth0';
import {
  clearRefreshToken,
  getRefreshToken,
  setAccessTokenOnService,
  storeRefreshToken,
} from './token-storage';
import { scheduleTokenRefresh } from './token-refresh-scheduler';

/**
 * True only when the error definitively proves the refresh token is dead —
 * Auth0 returned 400 (invalid_grant) or 401. Every other failure (network,
 * 5xx/429, or an unknown status such as the desktop path's collapsed error) is
 * transient: we must NOT clear the token, because destroying a valid session on
 * an unprovable failure is the bug this guards against. A non-AuthRefreshError
 * (e.g. a fetchUserInfo failure after the refresh already succeeded) is likewise
 * treated as transient — the token is valid.
 */
function isDefinitiveAuthRejection(err: unknown): boolean {
  return err instanceof AuthRefreshError && (err.status === 400 || err.status === 401);
}

/**
 * Resume an OAuth session on app boot. Runs on both web and desktop.
 *
 * The flow:
 *   1. Read the stored refresh_token (tauri-plugin-store on desktop,
 *      sessionStorage on web)
 *   2. Call Auth0's /oauth/token with grant_type=refresh_token
 *   3. Push the new access_token into native HTTP clients + auth store
 *   4. Persist the rotated refresh_token (Auth0 rotates on each use when
 *      Refresh Token Rotation is enabled)
 *
 * On a DEFINITIVE rejection (Auth0 400 invalid_grant / 401): clear the stored
 * token and show sign-in — the token is genuinely dead. On a TRANSIENT failure
 * (no stored token aside, network error, Auth0 5xx/429, unknown-status desktop
 * error, or a userinfo failure after a successful refresh): leave the token in
 * place so the next launch can resume, and just show sign-in for this session.
 * We never throw — failures are silent so a bad cached token doesn't crash app
 * startup, and a transient blip never destroys a valid session.
 */
export function useSessionResume() {
  useEffect(() => {

    let cancelled = false;
    void (async () => {
      try {
        const refreshToken = await getRefreshToken();
        if (!refreshToken) {
          console.log('[useSessionResume] no stored refresh token — sign-in required');
          useAuthStore.setState({ isLoading: false });
          return;
        }

        console.log('[useSessionResume] refreshing session…');
        const tokens = await refreshAccessToken(refreshToken);
        if (cancelled) return;

        // Auth0 rotates refresh tokens on each use (Refresh Token Rotation
        // is enabled on the SPA) — store the new one. If absent, keep using
        // the old one (Auth0 may also be configured non-rotating per-tenant).
        if (tokens.refresh_token) {
          await storeRefreshToken(tokens.refresh_token);
        }

        const userInfo = await fetchUserInfo(tokens.access_token);
        if (cancelled) return;

        await setAccessTokenOnService(tokens.access_token);
        scheduleTokenRefresh(tokens.expires_in);
        useAuthStore.getState().setFromAuth({
          user: {
            sub: userInfo.sub,
            email: userInfo.email,
            name: userInfo.name ?? '',
            picture: userInfo.picture ?? '',
          },
          accessToken: tokens.access_token,
          isAuthenticated: true,
          isLoading: false,
        });
        posthog.identify(userInfo.sub, { email: userInfo.email, name: userInfo.name ?? '' });
        console.log('[useSessionResume] session resumed');
      } catch (err) {
        // Only clear the token on a DEFINITIVE rejection (Auth0 says the refresh
        // token is dead: 400 invalid_grant / 401). A transient failure — network
        // blip at startup, Auth0 5xx/429, an unknown-status desktop error, or a
        // fetchUserInfo failure AFTER the refresh already succeeded — must NOT
        // destroy a possibly-valid session; keep the token so the next launch
        // resumes. Either way, stop loading so the user isn't stuck on the splash.
        if (isDefinitiveAuthRejection(err)) {
          console.warn('[useSessionResume] refresh token rejected — clearing', err);
          await clearRefreshToken();
        } else {
          console.warn('[useSessionResume] resume failed transiently — keeping token', err);
        }
        if (!cancelled) {
          useAuthStore.setState({ isLoading: false });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);
}
